import { Edit } from '@mui/icons-material'
import LockResetIcon from '@mui/icons-material/LockReset'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { PersonDto } from 'dtos'
import { PersonVTS2AccessDto } from 'dtos/personVTS2AccessDto'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import VTS2AccessAddEditDialog from './VST2AccessAddEditDialog'

interface Props {
  person: PersonDto
  setPerson: React.Dispatch<React.SetStateAction<PersonDto>>
}

export default function VTS2AccessCard({ person, setPerson }: Props) {
  const [isGettingVTS2AccessInformation, setIsGettingVTS2AccessInformation] = useState<boolean>(false)
  const [isSendingForgotPasswordEmail, setIsSendingForgotPasswordEmail] = useState<boolean>(false)
  const [isVTS2AccessSaving, setIsVTS2AccessSaving] = useState<boolean>(false)
  const [personVTS2Access, setPersonVTS2Access] = useState<PersonVTS2AccessDto>(new PersonVTS2AccessDto())
  const [VTS2AccessAddEditDialogOpen, setVTS2AccessAddEditDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (person.personGuid) {
      setIsGettingVTS2AccessInformation(true)
      controllers
        .getPersonVTS2Access(person.personGuid)
        .then(response => {
          setPersonVTS2Access(response.value)
        })
        .finally(() => {
          setIsGettingVTS2AccessInformation(false)
        })
    }
  }, [person.personGuid])

  return (
    <>
      <VTS2AccessAddEditDialog
        initialValues={personVTS2Access}
        loading={isVTS2AccessSaving}
        onClose={() => {
          setVTS2AccessAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsVTS2AccessSaving(true)
          controllers
            .updatePersonVTS2Access(person.personGuid!, values)
            .then(res => {
              enqueueSnackbar('VTS2 Access Saved Successfully!', { variant: 'success' })
              setPersonVTS2Access(res.value)
              // Refresh permissions immediately so the Statement of Conformity Signature card
              // is shown or hidden without requiring the user to refresh the page after the
              // Conformity Add/Edit permission is added or removed.
              // Maxwell Sommer 07/28/2026
              setPerson({ ...person, isConformitySigner: res.value.isConformitySigner })
              setVTS2AccessAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsVTS2AccessSaving(false)
            })
        }}
        open={VTS2AccessAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  VTS2 Access
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1} alignItems='center'>
                <Grid item>
                  <Button
                    disabled={isSendingForgotPasswordEmail || !(person.email && person.allowUserToSignIn)}
                    endIcon={<LockResetIcon />}
                    onClick={() => {
                      if (person.email) {
                        setIsSendingForgotPasswordEmail(true)
                        controllers
                          .sendPasswordReset(person.email)
                          .then(() => {
                            enqueueSnackbar(`Sent Forgot Password Email To "${person.email}" Successfully!`, {
                              variant: 'success'
                            })
                          })
                          .finally(() => {
                            setIsSendingForgotPasswordEmail(false)
                          })
                      }
                    }}
                    variant='text'
                  >
                    RESET PASSWORD
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    onClick={() => {
                      setIsVTS2AccessSaving(false)
                      setVTS2AccessAddEditDialogOpen(true)
                    }}
                    variant='text'
                    endIcon={<Edit />}
                  >
                    EDIT
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingVTS2AccessInformation && (
              <Grid item container alignItems='center' justifyContent='center'>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingVTS2AccessInformation && (
              <Grid item xs={12} container spacing={2} alignItems='center'>
                <Grid item xs={12}>
                  <LabelField label='Default Import Branch' value={personVTS2Access?.defaultImportBranch?.name ?? 'All Locations'} />
                </Grid>

                {personVTS2Access?.permissions?.map(permission => (
                  <Grid item xs={12} key={permission.permissionGuid}>
                    <Typography variant='body1'>{permission.name}</Typography>

                    <Typography variant='body2'>{permission.description}</Typography>
                  </Grid>
                ))}
              </Grid>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**VTS2 Access Card:**

This card is displayed only if the individual is a VTS2 User.

**RESET PASSWORD Button:**

Clicking the RESET PASSWORD button sends a "Forgot Password" email to the 
email address associated with the user.

**EDIT Button:**

Clicking the EDIT button opens the VTS2 Access Dialog, where the user’s access 
details can be modified.

**Listed Permissions:**

Displays all permissions currently assigned to the user. These permissions are for 
reference and can be updated via the VTS2 Access Dialog.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
